import React from 'react';
import PropTypes from 'prop-types';
import { useSuspense } from '@rest-hooks/react';
import { Link, useHistory, useParams } from 'react-router-dom';

import UserAccountResource from 'resources/user/UserAccountResource';

import Button from 'ui/Button';
import Description from 'ui/text/Description';

import { t } from "i18n/index";
import { Viewer } from '@toast-ui/react-editor';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

import { checkLoginStatus } from 'utils/checkLoginStatus';

const getProviderFromEmail = (email) => {
  if (email.toLowerCase().includes('hotmail')) return 'Hotmail';
  if (email.toLowerCase().includes('live.com')) return 'Live';
  if (email.toLowerCase().includes('outlook')) return 'Outlook';
  return null
};

const TicketDescriptions = ({ amount, ticketDescription, suffix, handleRedirectToBillingDetails }) => (
  <>
    <Description bold>{t('profile:memberships:claimModal:ticketDescription', {suffix})}:</Description>
    <Description preWrap>{ticketDescription}{amount > 1 && <Link to="#" onClick={handleRedirectToBillingDetails}>{t('literal:here')}</Link>}.</Description>
  </>
);

TicketDescriptions.propTypes = {
  amount: PropTypes.number,
  ticketDescription: PropTypes.string,
  suffix: PropTypes.string,
  handleRedirectToBillingDetails: PropTypes.func,
};

// Personalized post-purchase content based on the buyer's email. Kept in its
// own component because UserAccountResource extends AuthenticatedResource and
// would 401 for lite-account buyers — a 401 here triggers localStorage.clear
// and redirects to /login, wiping the purchase confirmation screen. Parent
// only mounts this when checkLoginStatus() is true.
const AuthenticatedPostPurchaseContent = ({ isTicket, amount, ticketDescription, suffix, handleRedirectToBillingDetails }) => {
  const { email } = useSuspense(UserAccountResource.detail(), { id: 1 });
  const provider = getProviderFromEmail(email);

  if (provider) {
    return (
      <>
        <Description bold>{t('profile:memberships:claimModal:microsoftEmailWarningTitle1')}</Description>
        <Description preWrap>{t('profile:memberships:claimModal:microsoftEmailWarningDescription1')}</Description>
        <Alert severity="warning">
          <AlertTitle>{t('profile:memberships:claimModal:microsoftEmailWarningTitle2', {provider})}:</AlertTitle>
          <Description preWrap>{t('profile:memberships:claimModal:microsoftEmailWarningDescription2')}</Description>
        </Alert>
      </>
    );
  }

  if (isTicket) {
    return (
      <TicketDescriptions
        amount={amount}
        ticketDescription={ticketDescription}
        suffix={suffix}
        handleRedirectToBillingDetails={handleRedirectToBillingDetails}
      />
    );
  }

  return null;
};

AuthenticatedPostPurchaseContent.propTypes = {
  isTicket: PropTypes.bool,
  amount: PropTypes.number,
  ticketDescription: PropTypes.string,
  suffix: PropTypes.string,
  handleRedirectToBillingDetails: PropTypes.func,
};

const PaymentProcessed = ({ setStep, handleClaimReset, stripe_purchase, isTicket, amount, content, organizationTitle, atTheDoor }) => {

  const {organizationSlug} = useParams();
  const history = useHistory()

  const isLoggedIn = checkLoginStatus();

  const handleRedirect = () => {
    handleClaimReset();
    history.push(`/${organizationSlug}`);
  };

  const handleRedirectToBillingDetails = () => {
    handleBillingDetails()
  };

  const handleTicketDetails = () => setStep(3);

  const handleBillingDetails = () => setStep(5);

  const description = stripe_purchase ? t('profile:memberships:claimModal:thankYouForPayment') : t('profile:memberships:claimModal:welcomeToTheClub');

  const singleTicketDescription = atTheDoor ? t('profile:memberships:claimModal:singleTicketAtTheDoorDescription') : t('profile:memberships:claimModal:singleTicketDescription');
  const multipleTicketDescription = atTheDoor ? t('profile:memberships:claimModal:multipleAtTheDoorTicketDescription') : t('profile:memberships:claimModal:multipleTicketDescription');
  const ticketDescription = amount === 1 ? singleTicketDescription : multipleTicketDescription;
  const suffix = amount === 1 ? "" : "s";

  const backButtonLabel = stripe_purchase ?  t('literal:close') : t('profile:memberships:claimModal:back');

  const nextButtonAction = isTicket && amount > 1 ? handleTicketDetails : handleBillingDetails;
  const nextButtonLabel = isTicket && amount > 1 ? t('profile:memberships:claimModal:ticketDetailsCTA') : t('profile:memberships:claimModal:billingDetailsCTA');

  const showMessageFrom = stripe_purchase;
  const showContentText = stripe_purchase && content;
  const showNextButton = stripe_purchase;

  return (
    <div className='payment-processed'>

      {showMessageFrom &&
        <Description bold>{t('literal:messageFrom', {organizationTitle})}:</Description>}

      {showContentText
        ? <Viewer
              initialValue={content}
              extendedAutolinks={true}
              linkAttributes={{
                  target: '_blank',
                  rel: 'noopener noreferrer',
              }}
          />
        : <Description>{description}</Description>}

      {isLoggedIn ? (
        <AuthenticatedPostPurchaseContent
          isTicket={isTicket}
          amount={amount}
          ticketDescription={ticketDescription}
          suffix={suffix}
          handleRedirectToBillingDetails={handleRedirectToBillingDetails}
        />
      ) : (
        isTicket && (
          <TicketDescriptions
            amount={amount}
            ticketDescription={ticketDescription}
            suffix={suffix}
            handleRedirectToBillingDetails={handleRedirectToBillingDetails}
          />
        )
      )}

      <div className='button-group'>
        <Button onClick={handleRedirect} fullwidth secondary>{backButtonLabel}</Button>

        {showNextButton && 
          <Button onClick={nextButtonAction} fullwidth highlighted>{nextButtonLabel}</Button>}

      </div>      
    </div>
  );
};

PaymentProcessed.propTypes = {
    setStep: PropTypes.func,
    handleClaimReset: PropTypes.func,
    stripe_purchase: PropTypes.bool,
    isTicket: PropTypes.bool,
    amount: PropTypes.number,
    content: PropTypes.object,
    organizationTitle: PropTypes.string,
    atTheDoor: PropTypes.bool
};

export default PaymentProcessed;

